import { CheckUserNameIdentities, SocialProvider } from '../types';
import {
  isAppleLoginOutageAdvisoryEnabled,
  isGoogleLoginOutageAdvisoryEnabled,
  isMicrosoftLoginOutageAdvisoryEnabled,
  isSlackLoginOutageAdvisoryEnabled,
} from '../utilities/social-login';
import { CoreApplication } from './applications';
import { AidFrontendConfigCollection } from './feature-flags/dynamic-config';

export type SocialLoginEligibility = {
  isEligibleForSocialLoginOnly: boolean;
  firstProductAccessed: CoreApplication | 'unknown';
  highlightedSocialProvider: SocialProvider | null;
  connectedSocialProviders: SocialProvider[];
  enabledSocialButtons: SocialProvider[];
  hasPassword: boolean | undefined;
};

export const defaultRankedSocialProviders = [
  SocialProvider.Google,
  SocialProvider.Microsoft,
  SocialProvider.Apple,
  SocialProvider.Slack,
];

/**
 * Ranks social providers from highest to lowest priority based on mapped predicates in socialProviderToPriorityPredicate.
 * @returns a new array containing the ordered rank of social providers. The first element is the highest rank.
 */
export const getRankedSocialProviders = (dynamicConfigs: AidFrontendConfigCollection) => {
  const highPriority: SocialProvider[] = [];
  const lowPriority: SocialProvider[] = [];

  const isProviderEnabled: Record<SocialProvider, boolean> = {
    [SocialProvider.Google]: !isGoogleLoginOutageAdvisoryEnabled(dynamicConfigs),
    [SocialProvider.Microsoft]: !isMicrosoftLoginOutageAdvisoryEnabled(dynamicConfigs),
    [SocialProvider.Apple]: !isAppleLoginOutageAdvisoryEnabled(dynamicConfigs),
    [SocialProvider.Slack]: !isSlackLoginOutageAdvisoryEnabled(dynamicConfigs),
  };

  defaultRankedSocialProviders.forEach(provider => {
    if (!isProviderEnabled[provider]) {
      lowPriority.push(provider);
    } else {
      // If it's enabled or undefined (not in the record), it's high priority
      highPriority.push(provider);
    }
  });

  return [...highPriority, ...lowPriority];
};

/**
 * Decide which social provider we should 'hero' or highlight. Full and partial outages are taken in to account.
 *
 * @param connected a list of social providers the user has connected to their atlassian account
 * @param dynamicConfigs collection of dynamic configs from the dynamic configs provider.
 *  Used for dynamic configuration statsig feature flags.
 * @returns a single social provider we should nudge the user to login with.
 */
export const getHighlightedSocialProvider = (
  connected: SocialProvider[],
  dynamicConfigs: AidFrontendConfigCollection
) => {
  const rankedSocialProviders = getRankedSocialProviders(dynamicConfigs);
  for (const socialProvider of rankedSocialProviders) {
    if (connected.includes(socialProvider)) {
      return socialProvider;
    }
  }
  return null;
};

export const getConnectedSocialProviders = (
  identities: CheckUserNameIdentities | void
): SocialProvider[] => {
  if (!identities) {
    return [];
  }
  return Object.values(SocialProvider).filter(provider => !!identities?.[provider]);
};

export const isEligibleForSocialLoginExperience = (
  socialProvider: SocialProvider | null,
  application: CoreApplication | undefined,
  hasPassword = true
) => Boolean(socialProvider && !hasPassword && application !== 'trello');

export const getSocialLoginEligibility = (
  identities: CheckUserNameIdentities | undefined,
  application: CoreApplication | undefined,
  dynamicConfigs: AidFrontendConfigCollection
): SocialLoginEligibility => {
  const connectedSocialProviders = getConnectedSocialProviders(identities);
  const highlightedSocialProvider = getHighlightedSocialProvider(
    connectedSocialProviders,
    dynamicConfigs
  );

  const hasPassword = identities?.password;

  const isEligibleForSocialLoginOnly = isEligibleForSocialLoginExperience(
    highlightedSocialProvider,
    application,
    hasPassword
  );

  return {
    isEligibleForSocialLoginOnly,
    highlightedSocialProvider,
    connectedSocialProviders,
    enabledSocialButtons: defaultRankedSocialProviders,
    firstProductAccessed: application || 'unknown',
    hasPassword,
  };
};
